import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  fetchMyCapabilities,
  fetchPermissionGrants,
  savePermissionGrants,
} from './api'
import { permissionKeys } from '@/lib/queryKeys'
import type { CapabilityGrant } from './grants'

/** Stored grants for the active org. Keyed by org so a switch cannot show `*`. */
export function usePermissionGrantsQuery() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: permissionKeys.grants(organization?.id),
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchPermissionGrants(token, signal)
    },
    retry: false,
  })
}

/**
 * Capability ids the signed-in user may use.
 *
 * Retried, unlike the matrix's own query: a failure here denies every guarded
 * surface, so a transient one must not read as "this organization holds
 * nothing".
 *
 * Never served stale, and re-read on tab focus and every 30 seconds, so a
 * role or matrix change reaches an open page without a reload.
 */
export function useMyCapabilitiesQuery() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: permissionKeys.mine(organization?.id),
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchMyCapabilities(token, signal)
    },
    retry: 2,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000,
  })
}

/**
 * Whether the signed-in user may use a capability.
 *
 * `undefined` means still loading; callers hold rather than answer.
 *
 * A settled failure is a denial. A capability an organization does not hold
 * must not be reachable by anyone in it, and a failed lookup is not evidence
 * that it holds one — so the surface stays shut rather than open. The query
 * retries before reporting failure, so a blip does not empty the nav.
 */
export function useCapability(capabilityId: string): boolean | undefined {
  const { data, isError } = useMyCapabilitiesQuery()

  if (isError) return false
  if (!data) return undefined
  return data.capabilities.includes(capabilityId)
}

/**
 * Every capability the signed-in user may use, as a set.
 *
 * For callers that test several capabilities at once — a nav list, a command
 * palette — where `useCapability` per id would mean a hook inside a loop over a
 * list whose length changes with the org.
 *
 * `null` means still loading; callers hold rather than answer. A settled
 * failure is an empty set, the same denial `useCapability` reports and for the
 * same reason: a failed lookup is not evidence the organization holds anything.
 */
export function useMyCapabilitySet(): Set<string> | null {
  const { data, isError } = useMyCapabilitiesQuery()

  return useMemo(() => {
    if (isError) return new Set<string>()
    if (!data) return null
    return new Set(data.capabilities)
  }, [data, isError])
}

export function useSavePermissionGrantsMutation() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (grants: Record<string, CapabilityGrant>) => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return savePermissionGrants(token, grants)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: permissionKeys.grantsAll,
      })
      // A save can change what the saver themselves may reach.
      void queryClient.invalidateQueries({ queryKey: permissionKeys.mineAll })
    },
  })
}
